import React, { Fragment, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Collapse, Form, Row, Spinner, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatSalesCurrency, formatSalesDate } from './salesPageUtils';
import { getSalesId, getSalesValue, useSalesApiList } from '../hooks/useSalesApiResource';

const PROJECT_STATUS_LOOKUP_TYPE_ID = 38;
const SALES_QUOTE_STATUS_LOOKUP_TYPE_ID = 17;
const LEGACY_PROJECT_STATUS_OPTIONS = [
  { id: 1, name: 'Open' },
  { id: 2, name: 'Sold' },
  { id: 3, name: 'Lost' },
];

const normalizeProject = (project = {}) => ({
  ...project,
  id: Number(getSalesId(project) || 0),
  name: String(getSalesValue(project, 'name', 'Name') || ''),
  projectNumber: String(getSalesValue(project, 'projectNumber', 'ProjectNumber', 'id', 'Id') || ''),
  companyId: Number(getSalesValue(project, 'companyId', 'CompanyId') || 0),
  companyName: String(getSalesValue(project, 'companyName', 'CompanyName') || ''),
  contactEmail: String(getSalesValue(project, 'contactEmail', 'ContactEmail') || ''),
  city: String(getSalesValue(project, 'city', 'City') || ''),
  state: String(getSalesValue(project, 'state', 'State') || ''),
  saleRepId: Number(getSalesValue(project, 'saleRepId', 'SaleRepId') || 0),
  saleRepName: String(getSalesValue(project, 'saleRepName', 'SaleRepName', 'saleRepFullName', 'SaleRepFullName') || ''),
  statusId: Number(getSalesValue(project, 'statusId', 'StatusId') || 0),
  closeDate: getSalesValue(project, 'closeDate', 'CloseDate'),
});

const normalizeQuote = (quote = {}) => ({
  id: Number(getSalesId(quote) || 0),
  projectId: Number(getSalesValue(quote, 'projectId', 'ProjectId') || 0),
  quotationNumber: String(getSalesValue(quote, 'quotationNumber', 'QuotationNumber') || ''),
  modelNumber: String(getSalesValue(quote, 'modelNumber', 'ModelNumber') || ''),
  statusId: Number(getSalesValue(quote, 'statusId', 'StatusId') || 0),
  createdBy: String(getSalesValue(quote, 'createdBy', 'CreatedBy') || ''),
  createdDate: String(getSalesValue(quote, 'createdDate', 'CreatedDate') || ''),
  finalPrice: Number(getSalesValue(quote, 'finalPrice', 'FinalPrice', 'totalPrice', 'TotalPrice') || 0),
});

const normalizeLookup = (lookup = {}) => ({
  id: Number(getSalesId(lookup) || 0),
  name: String(getSalesValue(lookup, 'name', 'Name', 'description', 'Description') || ''),
  lookupTypeId: Number(getSalesValue(lookup, 'lookupTypeId', 'LookupTypeId') || 0),
});

const normalizeSalesRepresentative = (representative = {}) => {
  const firstName = String(getSalesValue(representative, 'firstName', 'FirstName') || '');
  const lastName = String(getSalesValue(representative, 'lastName', 'LastName') || '');
  const fullName = String(getSalesValue(representative, 'fullName', 'FullName', 'name', 'Name') || `${firstName} ${lastName}`.trim());
  return {
    id: Number(getSalesId(representative) || getSalesValue(representative, 'saleRepId', 'SaleRepId', 'userId', 'UserId') || 0),
    fullName,
  };
};

const normalizeProjects = (rows) => rows.map(normalizeProject);
const normalizeQuotes = (rows) => rows.map(normalizeQuote);
const normalizeLookups = (rows) => rows.map(normalizeLookup);
const normalizeSalesRepresentatives = (rows) => rows.map(normalizeSalesRepresentative).filter((rep) => rep.id);

const getStatusVariant = (status) => {
  if (status === 'Approved') return 'success';
  if (status === 'ProposalCreated') return 'info';
  if (status === 'Sold') return 'primary';
  return 'secondary';
};

const ProjectList = () => {
  const { canEdit } = usePermissions();
  const canEditSales = canEdit('sales.container');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [actionError, setActionError] = useState('');

  const {
    items: projects,
    setItems: setProjects,
    loading: projectsLoading,
    error: projectsError,
    request,
    endpoints,
  } = useSalesApiList((salesEndpoints) => salesEndpoints.projects.base, {
    normalize: normalizeProjects,
    errorMessage: 'Unable to load projects.',
  });
  const { items: quotes, loading: quotesLoading, error: quotesError } = useSalesApiList((salesEndpoints) => salesEndpoints.salesQuotes.base, {
    normalize: normalizeQuotes,
    errorMessage: 'Unable to load sales quotes.',
  });
  const { items: statusOptions } = useSalesApiList((salesEndpoints) => salesEndpoints.lookups.base, {
    normalize: normalizeLookups,
    errorMessage: 'Unable to load status options.',
  });
  const { items: saleRepresentatives } = useSalesApiList((salesEndpoints) => salesEndpoints.saleRepresentatives.base, {
    normalize: normalizeSalesRepresentatives,
    errorMessage: 'Unable to load sales representatives.',
  });

  const projectStatusById = useMemo(() => new Map([
    ...LEGACY_PROJECT_STATUS_OPTIONS,
    ...statusOptions.filter((status) => status.lookupTypeId === PROJECT_STATUS_LOOKUP_TYPE_ID),
  ].map((status) => [status.id, status.name])), [statusOptions]);
  const quoteStatusById = useMemo(() => new Map(statusOptions.filter((status) => status.lookupTypeId === SALES_QUOTE_STATUS_LOOKUP_TYPE_ID).map((status) => [status.id, status.name])), [statusOptions]);
  const saleRepById = useMemo(() => new Map(saleRepresentatives.map((rep) => [rep.id, rep.fullName])), [saleRepresentatives]);
  const quotesByProjectId = useMemo(() => {
    const grouped = new Map();
    quotes.forEach((quote) => {
      const current = grouped.get(quote.projectId) || [];
      current.push(quote);
      grouped.set(quote.projectId, current);
    });
    return grouped;
  }, [quotes]);

  const filteredProjects = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    if (!needle) return projects;
    return projects.filter((project) =>
      [project.name, project.projectNumber, project.companyName, project.contactEmail, project.state, project.saleRepName]
        .some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [projects, searchTerm]);

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects((current) => {
      const next = new Set(current);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const handleStatusChange = async (project, newStatusId) => {
    if (!canEditSales) return;
    const payload = { ...project, Id: project.id, StatusId: newStatusId };
    setActionError('');

    try {
      await request({ method: 'PUT', url: endpoints.projects.byId(project.id), data: payload });
      setProjects((current) => current.map((item) => (item.id === project.id ? normalizeProject(payload) : item)));
      setEditingStatusId(null);
    } catch {
      setActionError('Unable to update project status.');
    }
  };

  const loading = projectsLoading || quotesLoading;

  return (
    <section className="sales-portal-page" aria-labelledby="sales-projects-title">
      <Row className="mb-3">
        <Col><h4 id="sales-projects-title">Sales Quote</h4></Col>
      </Row>
      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <div className="position-relative search-input">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#626262' }}></i>
              <Form.Control type="text" placeholder="Search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} style={{ paddingLeft: '40px' }} />
            </div>
          </Form.Group>
        </Col>
        <Col md={8} className="text-end">
          <Button as={Link} to="/sales/quotes/new" className="btn-success addbtnview me-2" style={{ minWidth: '200px' }}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Sales Quote
          </Button>
          <Button as={Link} to="/sales/projects" className="btn-success addbtnview" style={{ minWidth: '200px' }}>
            <i className="bi bi-plus-circle me-2"></i>
            Add New Project
          </Button>
        </Col>
      </Row>
      {(projectsError || quotesError || actionError) && <Alert variant="danger">{projectsError || quotesError || actionError}</Alert>}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: '50px' }}></th>
                  <th>Project Number</th>
                  <th>Project Name</th>
                  <th>Company</th>
                  <th>Contact Email</th>
                  <th>Location</th>
                  <th>Sales Rep</th>
                  <th>Status</th>
                  <th>Quotes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9}><div className="sales-no-data"><Spinner animation="border" size="sm" /> Loading projects...</div></td></tr>
                ) : filteredProjects.length === 0 ? (
                  <tr><td colSpan={9}><div className="sales-no-data">No projects found.</div></td></tr>
                ) : (
                  filteredProjects.map((project) => {
                    const isExpanded = expandedProjects.has(project.id);
                    const projectQuotes = quotesByProjectId.get(project.id) || [];
                    const statusLabel = projectStatusById.get(project.statusId) || `Status ${project.statusId || '-'}`;
                    const saleRepName = project.saleRepName || saleRepById.get(project.saleRepId) || project.saleRepId;
                    return (
                      <Fragment key={project.id}>
                        <tr>
                          <td>
                            <button type="button" className="edit-icon" onClick={() => toggleProjectExpansion(project.id)} aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${project.name}`}>
                              <i className={`bi bi-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                            </button>
                          </td>
                          <td>{project.projectNumber}</td>
                          <td>{project.name}</td>
                          <td>{project.companyName || project.companyId}</td>
                          <td>{project.contactEmail}</td>
                          <td>{[project.city, project.state].filter(Boolean).join(', ')}</td>
                          <td>{saleRepName}</td>
                          <td>
                            {editingStatusId === project.id ? (
                              <Form.Select size="sm" value={project.statusId} onChange={(event) => handleStatusChange(project, Number(event.target.value))}>
                                {[...projectStatusById.entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                              </Form.Select>
                            ) : (
                              <button type="button" className="btn btn-link p-0" disabled={!canEditSales} onClick={() => setEditingStatusId(project.id)}>
                                <Badge bg={getStatusVariant(statusLabel)}>{statusLabel}</Badge>
                              </button>
                            )}
                          </td>
                          <td><Badge bg="secondary">{projectQuotes.length}</Badge></td>
                        </tr>
                        <tr>
                          <td colSpan={9} className="p-0 border-0">
                            <Collapse in={isExpanded}>
                              <div className="p-3 bg-light">
                                <Table bordered hover size="sm" className="mb-0" style={{ backgroundColor: 'white' }}>
                                  <thead>
                                    <tr>
                                      <th>Quotation Number</th>
                                      <th>Model Number</th>
                                      <th>Status</th>
                                      <th>Created By</th>
                                      <th>Created Date</th>
                                      <th>Final Price</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {projectQuotes.length === 0 ? (
                                      <tr><td colSpan={7}><div className="sales-no-data">No quotes found for this project.</div></td></tr>
                                    ) : projectQuotes.map((quote) => {
                                      const quoteStatus = quoteStatusById.get(quote.statusId) || `Status ${quote.statusId || '-'}`;
                                      return (
                                        <tr key={quote.id}>
                                          <td>{quote.quotationNumber}</td>
                                          <td>{quote.modelNumber}</td>
                                          <td><Badge bg={getStatusVariant(quoteStatus)}>{quoteStatus}</Badge></td>
                                          <td>{quote.createdBy}</td>
                                          <td>{formatSalesDate(quote.createdDate)}</td>
                                          <td>{formatSalesCurrency(quote.finalPrice)}</td>
                                          <td>
                                            <Link to={`/sales/salesquote/${project.id}/details/${quote.id}`} className="edit-icon" title="Edit Quote">
                                              <i className="bi bi-pencil-square"></i>
                                            </Link>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </div>
                            </Collapse>
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectList;
